import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Badge, Button, Col, Container, Form, Modal, OverlayTrigger, Row, Tab, Tabs, Tooltip } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CompanyType, companyIntegrations, crmIntegrations } from "../../models/company";
import { useAppDispatch, useAppSelector } from "../../store";
import { companyActions } from "../../store/company-slice";
import {
  activateIntegrationPipeline,
  fetchCompany,
  fetchCompanyIntegration,
  fetchCompanyServiceConfig,
  formatSaveError,
  patchCompany,
  postCompany,
  triggerIntegrationNormalize,
  triggerIntegrationPoll,
} from "../../utils/http/companies";
import { queryClient } from "../../utils/http/queryClient";
import { switchToCustomer } from "../../loaders/customer-override";
import CompanyGeneral from "./CompanyGeneral";
import CompanyIntegrations from "./CompanyIntegrations";
import CompanyMergeConfig from "./CompanyMergeConfig";
import CompanyServices from "./CompanyServices";
import CompanyUsers from "./CompanyUsers";

function CompanyDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyName } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user);
  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const canViewUsers = currentUser?.roles?.includes("users-view") || false;
  const canChangeCompanies = currentUser?.roles?.includes("companies-change") || false;
  const canTriggerPolling = currentUser?.roles?.includes("windmill-trigger-polling") || false;
  const canTriggerNormalization = currentUser?.roles?.includes("windmill-trigger-normalization") || false;

  const [company, setCompany] = useState<CompanyType | null>(null);
  const [originalCompany, setOriginalCompany] = useState<CompanyType | null>(null);
  const [mergeConfigJson, setMergeConfigJson] = useState("{}");
  const [originalMergeConfigJson, setOriginalMergeConfigJson] = useState("{}");
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [officeKeysError, setOfficeKeysError] = useState<string | null>(null);
  const [officeKeysResetKey, setOfficeKeysResetKey] = useState(0);
  const [selectedService, setSelectedService] = useState<string>("popup");
  const [selectedAutomation, setSelectedAutomation] = useState<string>("visit");
  const [selectedIntegration, setSelectedIntegration] = useState<string>("whise");
  const [activeTab, setActiveTab] = useState<string>("general");
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    type: "service" | "automation" | "integration" | null;
    itemName: string;
  }>({ show: false, type: null, itemName: "" });
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [loadedIntegrations, setLoadedIntegrations] = useState<Set<string>>(new Set());
  const [loadedServices, setLoadedServices] = useState<Set<string>>(new Set());
  const [detailLoading, setDetailLoading] = useState<string | null>(null);

  const serviceTypes = ["popup", "automation", "dashboard", "tracker"];
  const serviceStatuses = ["inactive", "onboarding", "active"];
  const automationTypes = [
    "visit",
    "visit-request",
    "inboundlead",
    "deal",
    "mariage",
    "listing-live",
    "activity-report",
    "invalid-data",
  ];
  const automationStatuses = ["disabled", "live", "colibry-testing", "customer-testing"];
  const integrationTypes = [
    "whise",
    "pipedrive",
    "zabun",
    "zabun360",
    "omnicasa",
    "dotdigital",
    "sendgrid",
    "hubspot",
    "skarabee",
    "salesforce",
    "smstools",
  ];
  const integrationStatuses = ["inactive", "onboarding", "active"];

  const isNew = companyName === "new";

  const { data: fetchedCompany, isLoading } = useQuery({
    queryKey: ["company", companyName],
    queryFn: ({ signal }) => fetchCompany({ companyNameOrId: companyName!, signal }),
    enabled: !isNew && !!companyName,
    staleTime: 2000,
  });

  // Get active services, automations, and integrations
  const activeServices = serviceTypes.filter((name) => company?.services?.[name]);
  const activeAutomations = automationTypes.filter((name) => company?.services?.automation?.specific_configs?.[name]);
  const activeIntegrations = integrationTypes.filter((name) => company?.integrations?.[name]);

  // Get available services, automations, and integrations (not yet added)
  const availableServices = serviceTypes.filter((name) => !company?.services?.[name]);
  const availableAutomations = automationTypes.filter(
    (name) => !company?.services?.automation?.specific_configs?.[name],
  );
  const availableIntegrations = integrationTypes.filter((name) => !company?.integrations?.[name]);

  useEffect(() => {
    if (isNew) {
      const newCompany = {
        name: "",
        status: "onboarding" as const,
        info: {
          name: "",
          languages: { nl: "default" as const },
        },
        services: {},
        config_design_temp: {},
      };
      setCompany(newCompany);
      setOriginalCompany(newCompany);
      setMergeConfigJson("{}");
      setOriginalMergeConfigJson("{}");
    } else if (fetchedCompany) {
      setCompany(fetchedCompany);
      setOriginalCompany(JSON.parse(JSON.stringify(fetchedCompany))); // Deep clone
      const mergeConfig = JSON.stringify(fetchedCompany.merge_config || {}, null, 2);
      setMergeConfigJson(mergeConfig);
      setOriginalMergeConfigJson(mergeConfig);
      setLoadedIntegrations(new Set());
      setLoadedServices(new Set());
    }
  }, [fetchedCompany, isNew]);

  useEffect(() => {
    // Read tab and sub-selection from URL hash on mount
    const hash = location.hash.replace("#", "");
    if (hash) {
      const [tab, subSelection] = hash.split(":");
      // Backwards compatibility: #automations:visit -> redirect to #services:automation
      if (tab === "automations") {
        setActiveTab("services");
        setSelectedService("automation");
        if (subSelection && automationTypes.includes(subSelection)) {
          setSelectedAutomation(subSelection);
        }
        window.location.hash = `services:automation`;
        return;
      }
      if (["general", "services", "integrations", "users", "merge"].includes(tab)) {
        setActiveTab(tab);
        if (tab === "services" && subSelection && serviceTypes.includes(subSelection)) {
          setSelectedService(subSelection);
        }
        if (tab === "integrations" && subSelection && integrationTypes.includes(subSelection)) {
          setSelectedIntegration(subSelection);
        }
      }
    }
  }, [location.hash]);

  // Set default selected item when active items change
  useEffect(() => {
    if (activeServices.length > 0 && !activeServices.includes(selectedService)) {
      setSelectedService(activeServices[0]);
    }
  }, [activeServices, selectedService]);

  useEffect(() => {
    if (activeAutomations.length > 0 && !activeAutomations.includes(selectedAutomation)) {
      setSelectedAutomation(activeAutomations[0]);
    }
  }, [activeAutomations, selectedAutomation]);

  useEffect(() => {
    if (activeIntegrations.length > 0 && !activeIntegrations.includes(selectedIntegration)) {
      setSelectedIntegration(activeIntegrations[0]);
    }
  }, [activeIntegrations, selectedIntegration]);

  // Lazy-fetch integration details when selected
  useEffect(() => {
    if (!originalCompany || isNew || activeTab !== "integrations") return;
    if (!originalCompany.integrations?.[selectedIntegration]) return;
    if (loadedIntegrations.has(selectedIntegration)) return;

    const abortController = new AbortController();
    setDetailLoading(`integration:${selectedIntegration}`);

    fetchCompanyIntegration({
      companyNameOrId: companyName!,
      integrationName: selectedIntegration,
      signal: abortController.signal,
    })
      .then((integrationData) => {
        setOriginalCompany((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            integrations: {
              ...prev.integrations,
              [selectedIntegration]: integrationData,
            },
          };
        });

        setCompany((prev) => {
          if (!prev) return prev;
          const existingStatus = prev.integrations?.[selectedIntegration]?.status;
          return {
            ...prev,
            integrations: {
              ...prev.integrations,
              [selectedIntegration]: {
                ...integrationData,
                ...(existingStatus !== undefined && existingStatus !== integrationData.status
                  ? { status: existingStatus }
                  : {}),
              },
            },
          };
        });

        setLoadedIntegrations((prev) => new Set(prev).add(selectedIntegration));
        setDetailLoading(null);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch integration detail:", err);
          setDetailLoading(null);
        }
      });

    return () => abortController.abort();
  }, [selectedIntegration, activeTab, originalCompany, loadedIntegrations]);

  // Lazy-fetch service details when selected
  useEffect(() => {
    if (!originalCompany || isNew || activeTab !== "services") return;
    if (!originalCompany.services?.[selectedService]) return;
    if (loadedServices.has(selectedService)) return;

    const abortController = new AbortController();
    setDetailLoading(`service:${selectedService}`);

    fetchCompanyServiceConfig({
      companyNameOrId: companyName!,
      serviceName: selectedService,
      signal: abortController.signal,
    })
      .then((serviceData) => {
        // Update both originalCompany and company with the same full data
        // so that change detection works correctly
        setOriginalCompany((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            services: {
              ...prev.services,
              [selectedService]: serviceData,
            },
          };
        });

        setCompany((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            services: {
              ...prev.services,
              [selectedService]: serviceData,
            },
          };
        });

        setLoadedServices((prev) => new Set(prev).add(selectedService));
        setDetailLoading(null);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch service detail:", err);
          setDetailLoading(null);
        }
      });

    return () => abortController.abort();
  }, [selectedService, activeTab, originalCompany, loadedServices]);

  const updateHash = (tab: string, subSelection?: string) => {
    if (subSelection) {
      window.location.hash = `${tab}:${subSelection}`;
    } else {
      window.location.hash = tab;
    }
  };

  // Normalize a value for comparison: strip nulls, empty strings, and empty objects/arrays
  // so that null vs undefined vs [] vs {} don't trigger false positives.
  const normalizeForCompare = (val: any): string => {
    return JSON.stringify(val, (_key, v) => {
      if (v === null || v === "" || v === undefined) return undefined;
      if (Array.isArray(v) && v.length === 0) return undefined;
      if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return undefined;
      return v;
    });
  };

  // Helper functions to check for changes
  const hasGeneralChanges = () => {
    if (!company || !originalCompany) return false;
    return (
      company.name !== originalCompany.name ||
      company.status !== originalCompany.status ||
      normalizeForCompare(company.info) !== normalizeForCompare(originalCompany.info) ||
      normalizeForCompare(company.legacy_data) !== normalizeForCompare(originalCompany.legacy_data) ||
      normalizeForCompare(company.config_design_temp) !== normalizeForCompare(originalCompany.config_design_temp)
    );
  };

  const hasServiceChanges = (serviceName: string) => {
    if (!company || !originalCompany) return false;
    return JSON.stringify(company.services?.[serviceName]) !== JSON.stringify(originalCompany.services?.[serviceName]);
  };

  const hasServicesTabChanges = () => {
    if (!company || !originalCompany) return false;
    return JSON.stringify(company.services) !== JSON.stringify(originalCompany.services);
  };

  const hasAutomationChanges = (automationType: string) => {
    if (!company || !originalCompany) return false;
    return (
      JSON.stringify(company.services?.automation?.specific_configs?.[automationType]) !==
      JSON.stringify(originalCompany.services?.automation?.specific_configs?.[automationType])
    );
  };

  const hasIntegrationChanges = (integrationName: string) => {
    if (!company || !originalCompany) return false;
    return (
      JSON.stringify(company.integrations?.[integrationName]) !==
      JSON.stringify(originalCompany.integrations?.[integrationName])
    );
  };

  const hasIntegrationsTabChanges = () => {
    if (!company || !originalCompany) return false;
    return JSON.stringify(company.integrations) !== JSON.stringify(originalCompany.integrations);
  };

  const hasMergeConfigChanges = () => {
    return mergeConfigJson !== originalMergeConfigJson;
  };

  const hasAnyChanges = () => {
    return hasGeneralChanges() || hasServicesTabChanges() || hasIntegrationsTabChanges() || hasMergeConfigChanges();
  };

  // Helper to check if a specific field has changed
  const hasFieldChanged = (currentValue: any, originalValue: any) => {
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  };

  const handleTabSelect = (tab: string | null) => {
    if (tab) {
      setActiveTab(tab);
      if (tab === "services") {
        updateHash(tab, selectedService);
      } else if (tab === "integrations") {
        updateHash(tab, selectedIntegration);
      } else {
        updateHash(tab);
      }
    }
  };

  const handleServiceSelect = (serviceName: string) => {
    setSelectedService(serviceName);
    updateHash("services", serviceName);
  };

  const handleIntegrationSelect = (integrationType: string) => {
    setSelectedIntegration(integrationType);
    updateHash("integrations", integrationType);
  };

  const handleAddService = (serviceName: string) => {
    if (!company) return;
    const updatedServices = { ...company.services };
    if (!updatedServices[serviceName]) {
      updatedServices[serviceName] = { status: "inactive" };
    }
    setCompany({ ...company, services: updatedServices });
    setSelectedService(serviceName);
    updateHash("services", serviceName);
    setLoadedServices((prev) => new Set(prev).add(serviceName));
  };

  const handleAddAutomation = (automationType: string) => {
    if (!company) return;
    const updatedServices = { ...company.services };

    // Deep clone automation service to avoid mutating original
    if (!updatedServices.automation) {
      updatedServices.automation = { status: "active", specific_configs: {} };
    } else {
      updatedServices.automation = {
        ...updatedServices.automation,
        specific_configs: { ...updatedServices.automation.specific_configs },
      };
    }

    if (!updatedServices.automation.specific_configs[automationType]) {
      updatedServices.automation.specific_configs[automationType] = { status: "disabled" };
    }

    setCompany({ ...company, services: updatedServices });
    setSelectedAutomation(automationType);
    setActiveTab("services");
    setSelectedService("automation");
    updateHash("services", "automation");
  };

  const handleAddIntegration = (integrationName: string) => {
    if (!company) return;
    const updatedIntegrations = { ...company.integrations };
    if (!updatedIntegrations[integrationName]) {
      updatedIntegrations[integrationName] = { status: "inactive" };
    }
    setCompany({ ...company, integrations: updatedIntegrations });
    setSelectedIntegration(integrationName);
    updateHash("integrations", integrationName);
    setLoadedIntegrations((prev) => new Set(prev).add(integrationName));
  };

  const handleDeleteAutomation = (automationType: string) => {
    setSelectedAutomation(automationType);
    setDeleteModal({ show: true, type: "automation", itemName: automationType });
  };

  const handleConfirmDelete = () => {
    if (!company || !deleteModal.type) return;

    if (deleteModal.type === "service") {
      const updatedServices = { ...company.services };
      delete updatedServices[selectedService];
      setCompany({ ...company, services: updatedServices });

      // Select another service if there are any remaining
      const remainingServices = activeServices.filter((s) => s !== selectedService);
      if (remainingServices.length > 0) {
        setSelectedService(remainingServices[0]);
        updateHash("services", remainingServices[0]);
      }
    } else if (deleteModal.type === "automation") {
      const updatedServices = { ...company.services };
      if (updatedServices.automation?.specific_configs) {
        delete updatedServices.automation.specific_configs[selectedAutomation];
      }
      setCompany({ ...company, services: updatedServices });

      // Select another automation if there are any remaining
      const remainingAutomations = activeAutomations.filter((a) => a !== selectedAutomation);
      if (remainingAutomations.length > 0) {
        setSelectedAutomation(remainingAutomations[0]);
      }
    } else if (deleteModal.type === "integration") {
      const updatedIntegrations = { ...company.integrations };
      delete updatedIntegrations[selectedIntegration];
      setCompany({ ...company, integrations: updatedIntegrations });

      // Select another integration if there are any remaining
      const remainingIntegrations = activeIntegrations.filter((i) => i !== selectedIntegration);
      if (remainingIntegrations.length > 0) {
        setSelectedIntegration(remainingIntegrations[0]);
        updateHash("integrations", remainingIntegrations[0]);
      }
    }

    setDeleteModal({ show: false, type: null, itemName: "" });
  };

  const { mutate: mutateSave } = useMutation({
    mutationFn: patchCompany,
    onSuccess: (data) => {
      // Update the query cache directly with the returned data
      queryClient.setQueryData(["company", companyName], data);

      // Only invalidate the companies list to refresh it
      queryClient.invalidateQueries({ queryKey: ["companies"] });

      setSaving(false);

      // Update local state with the saved data
      setCompany(data);
      setOriginalCompany(JSON.parse(JSON.stringify(data)));
      setMergeConfigJson(JSON.stringify(data.merge_config || {}, null, 2));
      setOriginalMergeConfigJson(JSON.stringify(data.merge_config || {}, null, 2));

      // Reset lazy-load tracking so details are re-fetched with fresh data
      setLoadedIntegrations(new Set());
      setLoadedServices(new Set());

      // If this is the active company, update it in Redux state
      if (activeCompany?.id === data.id) {
        dispatch(companyActions.setActiveCompany(data));
      }
    },
    onError: (error: Error) => {
      setSaving(false);
      setParseError(formatSaveError(error));
    },
  });

  const { mutate: mutateCreate } = useMutation({
    mutationFn: postCompany,
    onSuccess: (data) => {
      // Only invalidate the companies list
      queryClient.invalidateQueries({ queryKey: ["companies"] });

      setSaving(false);

      // Update local state with the created data
      setCompany(data);
      setOriginalCompany(JSON.parse(JSON.stringify(data)));
      setMergeConfigJson(JSON.stringify(data.merge_config || {}, null, 2));
      setOriginalMergeConfigJson(JSON.stringify(data.merge_config || {}, null, 2));

      navigate(`/companies/${data.name}`);
    },
    onError: () => {
      setSaving(false);
    },
  });

  const { mutate: mutateActivatePipeline, isPending: isPipelineStarting } = useMutation({
    mutationFn: activateIntegrationPipeline,
    onSuccess: () => {
      // Refetch company data to get updated substatus
      queryClient.invalidateQueries({ queryKey: ["company", companyName] });
    },
    onError: (error: any) => {
      const errorMessage = error?.info?.detail || error?.message || "Failed to start activation pipeline";
      alert(`Error: ${errorMessage}`);
    },
  });

  const { mutate: mutateTriggerPoll, isPending: isPolling } = useMutation({
    mutationFn: triggerIntegrationPoll,
    onSuccess: () => {
      alert("Poll triggered successfully.");
    },
    onError: (error: any) => {
      const errorMessage = error?.info?.detail || error?.message || "Failed to trigger poll";
      alert(`Error: ${errorMessage}`);
    },
  });

  const { mutate: mutateTriggerNormalize, isPending: isNormalizing } = useMutation({
    mutationFn: triggerIntegrationNormalize,
    onSuccess: () => {
      alert("Normalization triggered successfully.");
    },
    onError: (error: any) => {
      const errorMessage = error?.info?.detail || error?.message || "Failed to trigger normalization";
      alert(`Error: ${errorMessage}`);
    },
  });

  // Deep diff function that tracks changes at the deepest level
  const deepDiff = (original: any, current: any, path: string = ""): any => {
    // If both are primitive or null/undefined, compare directly
    if (
      original === current ||
      (original === null && current === null) ||
      (original === undefined && current === undefined)
    ) {
      return undefined;
    }

    // If types are different or one is null/undefined, return the current value
    if (
      typeof original !== typeof current ||
      original === null ||
      original === undefined ||
      current === null ||
      current === undefined
    ) {
      return current;
    }

    // Handle arrays
    if (Array.isArray(original) && Array.isArray(current)) {
      if (JSON.stringify(original) !== JSON.stringify(current)) {
        return current;
      }
      return undefined;
    }

    // Handle objects
    if (typeof original === "object" && typeof current === "object") {
      const changes: any = {};
      const allKeys = new Set([...Object.keys(original), ...Object.keys(current)]);

      for (const key of allKeys) {
        const newPath = path ? `${path}.${key}` : key;

        if (!(key in current)) {
          // Field was removed - set to null
          changes[key] = null;
          console.log(`Field removed: ${newPath}`);
        } else if (!(key in original)) {
          // Field was added
          changes[key] = current[key];
          console.log(`Field added: ${newPath}`, current[key]);
        } else {
          // Field exists in both - check for changes
          const diff = deepDiff(original[key], current[key], newPath);
          if (diff !== undefined) {
            changes[key] = diff;
            console.log(`Field changed: ${newPath}`, { old: original[key], new: current[key] });
          }
        }
      }

      return Object.keys(changes).length > 0 ? changes : undefined;
    }

    // Primitive values that are different
    return current;
  };

  const handleRevert = () => {
    if (!originalCompany) return;
    setCompany(JSON.parse(JSON.stringify(originalCompany)));
    setMergeConfigJson(originalMergeConfigJson);
    setParseError(null);
    setOfficeKeysError(null);
    setOfficeKeysResetKey((key) => key + 1);
  };

  // Service `triggers` are edited as raw JSON text in the textarea; parse them into
  // real objects so Compass receives `triggers` as a dict rather than a string.
  const normalizeServiceTriggers = (comp: CompanyType): CompanyType => {
    if (!comp.services) return comp;

    const services: { [key: string]: any } = {};
    for (const [name, config] of Object.entries(comp.services)) {
      const serviceConfig = { ...(config as any) };
      if (typeof serviceConfig.triggers === "string") {
        const text = serviceConfig.triggers.trim();
        if (text === "") {
          delete serviceConfig.triggers;
        } else {
          try {
            serviceConfig.triggers = JSON.parse(text);
          } catch {
            throw new Error(`Invalid JSON in Triggers field for the "${name}" service`);
          }
        }
      }
      services[name] = serviceConfig;
    }

    return { ...comp, services };
  };

  const handleSave = () => {
    if (!company) return;

    setParseError(null);

    if (officeKeysError) {
      setParseError(officeKeysError);
      return;
    }

    try {
      const merge_config = JSON.parse(mergeConfigJson);

      const updatedCompany: CompanyType = {
        ...normalizeServiceTriggers(company),
        merge_config,
      };

      const originalCompanyWithMerge = {
        ...(originalCompany ? normalizeServiceTriggers(originalCompany) : originalCompany),
        merge_config: JSON.parse(originalMergeConfigJson),
      };

      setSaving(true);

      if (isNew) {
        mutateCreate({ name: updatedCompany.name, website_url: updatedCompany.info?.website_url });
      } else {
        // Calculate deep diff - this will send only changed fields
        const patch = deepDiff(originalCompanyWithMerge, updatedCompany);

        if (!patch) {
          // No changes to save
          setSaving(false);
          return;
        }

        console.log("Sending PATCH with changes:", patch);
        mutateSave({ companyNameOrId: company.name, patch });
      }
    } catch (error) {
      setParseError(
        error instanceof Error && error.message.includes("Triggers")
          ? error.message
          : "Invalid JSON in Merge Config field",
      );
      setSaving(false);
      return;
    }
  };

  const handleServiceStatusChange = (serviceName: string, status: string) => {
    if (!company) return;

    const updatedServices = { ...company.services };
    // Deep clone the service to avoid mutating originalCompany
    updatedServices[serviceName] = {
      ...updatedServices[serviceName],
      status,
    };

    setCompany({
      ...company,
      services: updatedServices,
    });
  };

  const handleServiceConfigChange = (serviceName: string, path: string[], value: any) => {
    if (!company) return;

    const updatedServices = { ...company.services };
    // Deep clone the service to avoid mutating originalCompany
    updatedServices[serviceName] = JSON.parse(JSON.stringify(updatedServices[serviceName] || {}));

    // Navigate to the nested path and set the value
    let current: any = updatedServices[serviceName];
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;

    setCompany({
      ...company,
      services: updatedServices,
    });
  };

  const handleAutomationConfigChange = (automationType: string, field: string, value: any) => {
    if (!company) return;

    const updatedServices = { ...company.services };
    // Deep clone automation service to avoid mutating originalCompany
    updatedServices.automation = JSON.parse(JSON.stringify(updatedServices.automation || {}));

    if (!updatedServices.automation.specific_configs) {
      updatedServices.automation.specific_configs = {};
    }
    if (!updatedServices.automation.specific_configs[automationType]) {
      updatedServices.automation.specific_configs[automationType] = {};
    }

    updatedServices.automation.specific_configs[automationType][field] = value;

    setCompany({
      ...company,
      services: updatedServices,
    });
  };

  const handleIntegrationStatusChange = (integrationName: string, status: string) => {
    if (!company) return;

    const updatedIntegrations = { ...company.integrations };
    // Deep clone the integration to avoid mutating originalCompany
    updatedIntegrations[integrationName] = {
      ...updatedIntegrations[integrationName],
      status: status as "inactive" | "onboarding" | "active",
    };

    setCompany({
      ...company,
      integrations: updatedIntegrations,
    });
  };

  const handleIntegrationConfigChange = (integrationName: string, path: string[], value: any) => {
    if (!company) return;

    const updatedIntegrations = { ...company.integrations };
    // Deep clone the integration to avoid mutating originalCompany
    updatedIntegrations[integrationName] = JSON.parse(JSON.stringify(updatedIntegrations[integrationName] || {}));

    // Navigate to the nested path and set the value
    let current: any = updatedIntegrations[integrationName];
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;

    setCompany({
      ...company,
      integrations: updatedIntegrations,
    });
  };

  const handleStartActivationPipeline = (integrationName: string, force = false) => {
    if (!company?.name) {
      alert("Company name is not available");
      return;
    }

    mutateActivatePipeline({
      companyNameOrId: company.name,
      integrationName: integrationName,
      force,
    });
  };

  const handleTriggerPoll = (integrationName: string, startTime?: string, entityTypes?: string[]) => {
    if (!company?.name) {
      alert("Company name is not available");
      return;
    }

    mutateTriggerPoll({
      companyNameOrId: company.name,
      integrationName,
      startTime,
      entityTypes,
    });
  };

  const handleTriggerNormalize = (integrationName: string, startTime?: string, entityTypes?: string[]) => {
    if (!company?.name) {
      alert("Company name is not available");
      return;
    }

    mutateTriggerNormalize({
      companyNameOrId: company.name,
      integrationName,
      startTime,
      entityTypes,
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!company) {
    return <div>Loading company...</div>;
  }

  // Changed indicator component
  const ChangedIndicator = () => (
    <span
      style={{
        display: "inline-block",
        width: "6px",
        height: "6px",
        backgroundColor: "#dc3545",
        borderRadius: "50%",
        marginLeft: "4px",
        verticalAlign: "super",
      }}
    />
  );

  const displayName = company?.info?.name || company?.name || "New Company";

  // CRM integrations that aren't switched off, e.g. whise or zabun360.
  const crms = crmIntegrations.filter(
    (name) => company?.integrations?.[name] && company.integrations[name].status !== "inactive",
  );

  return (
    <div>
      <Container fluid className="py-3">
        <div
          className="d-flex align-items-center justify-content-between p-3 mb-4"
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <div className="d-flex gap-2">
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Back to Companies</Tooltip>}>
              <Button variant="outline-secondary" size="sm" onClick={() => navigate("/companies")}>
                <i className="bi bi-arrow-left-circle"></i>
              </Button>
            </OverlayTrigger>
            {!isNew && activeCompany?.name !== company.name && (
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Select as Active Company</Tooltip>}>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    // Reuse the shared switch so we activate the clean, full
                    // server copy (not the local edit buffer) and go through the
                    // same access path as the `?customer=` override.
                    switchToCustomer(company.name);
                  }}
                >
                  <i className="bi bi-check-circle"></i>
                </Button>
              </OverlayTrigger>
            )}
          </div>

          <h2 className="mb-0 text-center flex-grow-1" style={{ fontSize: "1.75rem", fontWeight: "600" }}>
            {isNew ? "Create New Company" : displayName}
          </h2>

          {/* Fixed width keeps the title centered */}
          <div className="d-flex justify-content-end gap-2" style={{ width: "160px" }}>
            {!isNew &&
              crms.map((name) => {
                const status = company.integrations?.[name]?.status;
                return (
                  <OverlayTrigger key={name} placement="bottom" overlay={<Tooltip>CRM ({status})</Tooltip>}>
                    <Badge
                      pill
                      bg={status === "active" ? "primary" : "warning"}
                      style={{ fontSize: "0.8rem", fontWeight: 500 }}
                    >
                      {companyIntegrations.find((i) => i.id === name)?.name ?? name}
                    </Badge>
                  </OverlayTrigger>
                );
              })}
          </div>
        </div>

        {parseError && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-danger">{parseError}</div>
            </Col>
          </Row>
        )}

        {hasAnyChanges() && canChangeCompanies && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <span>You have unsaved changes</span>
                </div>
                <div className="d-flex gap-2">
                  <Button onClick={handleRevert} disabled={saving} variant="outline-secondary">
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Do not save
                  </Button>
                  <Button onClick={handleSave} disabled={saving} variant={saving ? "secondary" : "primary"}>
                    <i className="bi bi-floppy me-2"></i>
                    {saving ? "Saving..." : isNew ? "Create" : "Save"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>

      <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">
        <Tab
          eventKey="general"
          title={
            <>
              General
              {hasGeneralChanges() && <ChangedIndicator />}
            </>
          }
        >
          <CompanyGeneral
            company={company}
            originalCompany={originalCompany}
            isNew={isNew}
            setCompany={setCompany}
            hasFieldChanged={hasFieldChanged}
            ChangedIndicator={ChangedIndicator}
            readOnly={!canChangeCompanies}
          />
        </Tab>

        <Tab
          eventKey="services"
          title={
            <>
              Services
              {hasServicesTabChanges() && <ChangedIndicator />}
            </>
          }
        >
          <CompanyServices
            company={company}
            originalCompany={originalCompany}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            activeServices={activeServices}
            availableServices={availableServices}
            serviceStatuses={serviceStatuses}
            hasServiceChanges={hasServiceChanges}
            hasFieldChanged={hasFieldChanged}
            handleServiceSelect={handleServiceSelect}
            handleAddService={handleAddService}
            handleServiceStatusChange={handleServiceStatusChange}
            handleServiceConfigChange={handleServiceConfigChange}
            onDelete={() => setDeleteModal({ show: true, type: "service", itemName: selectedService })}
            ChangedIndicator={ChangedIndicator}
            readOnly={!canChangeCompanies}
            activeAutomations={activeAutomations}
            availableAutomations={availableAutomations}
            automationStatuses={automationStatuses}
            hasAutomationChanges={hasAutomationChanges}
            handleAutomationConfigChange={handleAutomationConfigChange}
            handleAddAutomation={handleAddAutomation}
            onDeleteAutomation={handleDeleteAutomation}
            detailLoading={detailLoading === `service:${selectedService}`}
          />
        </Tab>

        <Tab
          eventKey="integrations"
          title={
            <>
              Integrations
              {hasIntegrationsTabChanges() && <ChangedIndicator />}
            </>
          }
        >
          <CompanyIntegrations
            company={company}
            originalCompany={originalCompany}
            selectedIntegration={selectedIntegration}
            activeIntegrations={activeIntegrations}
            availableIntegrations={availableIntegrations}
            integrationStatuses={integrationStatuses}
            hasIntegrationChanges={hasIntegrationChanges}
            hasFieldChanged={hasFieldChanged}
            handleIntegrationSelect={handleIntegrationSelect}
            handleAddIntegration={handleAddIntegration}
            handleIntegrationStatusChange={handleIntegrationStatusChange}
            handleIntegrationConfigChange={handleIntegrationConfigChange}
            onDelete={() => setDeleteModal({ show: true, type: "integration", itemName: selectedIntegration })}
            onStartActivationPipeline={handleStartActivationPipeline}
            isPipelineStarting={isPipelineStarting}
            onPoll={handleTriggerPoll}
            isPolling={isPolling}
            canPoll={canTriggerPolling}
            onNormalize={handleTriggerNormalize}
            isNormalizing={isNormalizing}
            canNormalize={canTriggerNormalization}
            ChangedIndicator={ChangedIndicator}
            readOnly={!canChangeCompanies}
            onOfficeKeysErrorChange={setOfficeKeysError}
            officeKeysResetKey={officeKeysResetKey}
            detailLoading={detailLoading === `integration:${selectedIntegration}`}
          />
        </Tab>

        {canViewUsers && (
          <Tab eventKey="users" title="Users">
            <CompanyUsers company={company} />
          </Tab>
        )}

        {/* Merge Config tab temporarily hidden */}
        {false && (
          <Tab
            eventKey="merge"
            title={
              <>
                Merge Config
                {hasMergeConfigChanges() && <ChangedIndicator />}
              </>
            }
          >
            <CompanyMergeConfig mergeConfigJson={mergeConfigJson} setMergeConfigJson={setMergeConfigJson} />
          </Tab>
        )}
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteModal.show}
        onHide={() => {
          setDeleteModal({ show: false, type: null, itemName: "" });
          setDeleteConfirmationText("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the <strong className="text-capitalize">{deleteModal.itemName}</strong>{" "}
            {deleteModal.type}?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
          <Form.Group>
            <Form.Label>
              Type <strong className="text-capitalize">{deleteModal.itemName}</strong> to confirm:
            </Form.Label>
            <Form.Control
              type="text"
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder={deleteModal.itemName}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeleteModal({ show: false, type: null, itemName: "" });
              setDeleteConfirmationText("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handleConfirmDelete();
              setDeleteConfirmationText("");
            }}
            disabled={deleteConfirmationText.toLowerCase() !== deleteModal.itemName.toLowerCase()}
          >
            Delete {deleteModal.type && deleteModal.type.charAt(0).toUpperCase() + deleteModal.type.slice(1)}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CompanyDetail;
